import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Paper } from "@material-ui/core";
import { useSelector } from "react-redux";
import { IYoelloTheme } from "../../yoello-lib/modules/themes/IYoelloTheme";
import { createStyles, makeStyles } from '@material-ui/core/styles';
import EColourPanel from "../../interfaces/EColourPanel";
import createTippingFormRows from "../../formSchemas/tipping.form";
import { IReduxAppState } from "../../reducers";
import Loading from "../util/Loading";
import FormTitle from "../../yoello-lib/modules/dynamicForms/FormComponents/FormTitle";
import DynamicForm from "../../yoello-lib/modules/dynamicForms/DynamicForms";
import validationResolver from '../../formValidation/tipping.validation';
import "../../css/settings.css";
import useCheckPermissions from "../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../const/permissions.const";
import IDynamicFormRow from "../../yoello-lib/modules/dynamicForms/interfaces/IDynamicFormRow";
import { useTippingOptions } from "./useTippingOptions";
import { options } from "./util/options";
import { AlertStatusCard } from "../../yoello-lib/modules/components/UI/Alerts/AlertStatusCard";

const useStyles = makeStyles((theme: IYoelloTheme) =>
  createStyles({
    root: {
      padding: "2rem",
      marginBottom: '1.5625rem',
      "& form": {
        "& >div": {
          justifyContent: "start!important",
          "& >div:last-child": {
            flexBasis: "unset!important",
          },
          "& >div>div": {
            marginBottom: "0.625rem!important"
          }
        },
        "& .MuiSwitch-colorPrimary.Mui-disabled + .MuiSwitch-track":{
          backgroundColor:"rgba(0, 0, 0, 0.12)!important"
        }
      }
    },
    alert: {
      margin: "1.25rem 0"
    }

  }),
);

interface Props {
  venue_group_id?: string,
}

const TippingComponent = ({ venue_group_id  }: Props) => {
  
  const { t } = useTranslation();
  const classes = useStyles();
  const [formData, setFormData] = useState(null);
  const [selectedTipingOptions, setSelectedTipingOptions] = useState({});
  const { isLoading, createTipping, handleSubmit, apiData, groupDetails } = useTippingOptions(venue_group_id);
  const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.ORDER_SETTINGS_MANAGEMENT);

  const selectedVenueId = useSelector(
    (state: IReduxAppState) => state.selectedVenueId
  );

  const defaultTippingOptionId = useSelector(
    (state: IReduxAppState) => state.venue[selectedVenueId]?.venueSettings?.default_tipping_option_id
  );

  const tipping_enabled = useSelector(
    (state: IReduxAppState) =>
      state.venue[selectedVenueId]?.venueSettings?.tipping_enabled
  );

  const tippingOptionsOverride = useMemo(() => {
    return !venue_group_id && apiData &&
      apiData.length > 0 &&
      apiData[0].group_override
  }, [venue_group_id, apiData])

  const formRows:IDynamicFormRow[] = useMemo(()=>{
   return createTippingFormRows(selectedTipingOptions, setSelectedTipingOptions, t, venue_group_id);
   // eslint-disable-next-line
  },[selectedTipingOptions, formData])

  useEffect(() => {
    if (!apiData || !Array.isArray(apiData)) return;
    const formObj: any = { tipping: {}, tipping_enabled };
    const selectedItems: any = {
      "1": {
        enabled: true,
        tip_type: "NODEFAULT",
        name: t('tipping.NoDefault'),
        id: "1",
        fixed: false,
        sort_position: 0,
      }
    };
    if (apiData.length > 4) {
      /**Somehow there can be more than 4 - so if thats the case bubble up the enabled  */
      apiData.sort((a, b) => (a.enabled === b.enabled) ? 0 : a.enabled ? -1 : 1);
    }

    for (const option of options) {
      const index = apiData
        .findIndex((item) => option.sort_position === item.sort_position);
      formObj[option.keyName] = apiData[index];
      if (apiData[index] && apiData[index].enabled) {
        selectedItems[apiData[index].id] = apiData[index];
      }
    }
    if (venue_group_id && groupDetails) {
      setFormData({
        ...formObj, default_option_id: groupDetails.group_settings?.default_tipping_option_id ?? "1",
        tipping_enabled: groupDetails.group_settings.tipping_options_override
      });
    } else {
      setFormData({ ...formObj, default_option_id: defaultTippingOptionId ?? "1" });
    }
    setSelectedTipingOptions(selectedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData, tipping_enabled, groupDetails, venue_group_id]);

  useEffect(() => {
    if (apiData?.length === 0) {
      createTipping();
    }
    // eslint-disable-next-line  
  }, [apiData])

  return <Paper className={classes.root}>
    <div className="ProfileForm dialog" id="tipping" data-component-name="tipping-container">
      <FormTitle title={t("tipping.header")} />

      {tippingOptionsOverride && <div className={classes.alert}>
        <AlertStatusCard
          alertProps={{ severity: "info" }}
          alertMessage={
            <>
              {t("groups.tipping.info")}{" "}
              <b>
                {t("groups.tipping.subInfo")}
              </b>
            </>
          }
        />
      </div>}

      {isLoading || formData === null ? (
        <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Loading />
        </div>
      ) : (
        <DynamicForm
          hasSpacing={false}
          onHandleSubmit={handleSubmit}
          useFormOptions={{
            resolver: (data: any) => validationResolver(data, formData),
          }}
          disableAll={!hasPermission || tippingOptionsOverride}
          disableAllInteraction={onFailedInteraction}
          formData={formData!}
          formRows={formRows}
          formKeyName="submit"
          formButtons={{
            rightButton: {
              disabled: !hasPermission || tippingOptionsOverride,
              type: "submit",
              label: t("tipping.save"),
              color: EColourPanel.PRIMARY,
              fullWidth: false,
              style: {
                marginRight: "auto"
              }
            }
          }}
        />
      )}
    </div>
  </Paper>
};

export default TippingComponent;
